import { NOT_READY_REASON_LABELS, type QueueLane } from '@vagusx/shared';
import type { OutreachCampaign, QueueReasonCode } from '@vagusx/clients';

/**
 * Words for the codes the queue and results carry (ADR-318, R12). Exhaustive
 * on purpose: a new reason added to the server fails typecheck here rather
 * than reaching staff as a raw code.
 */
export const REASON_LABELS: Record<QueueReasonCode, string> = {
  ...NOT_READY_REASON_LABELS,

  // Why a try was deferred (skip reasons).
  outreach_disabled: 'Automated calling is switched off for the practice',
  campaign_inactive: 'The campaign is paused',
  window_closed: 'Outside calling hours',
  held_by_campaign: 'Another campaign is calling them',
  held_by_higher_priority: 'A higher-priority campaign goes first',
  cooldown: 'Too soon after the last contact',
  contact_ceiling: 'Reached the practice’s limit of contacts for now',
  voicemail_grace: 'Waiting after leaving a voicemail',
  at_capacity: 'As many calls as allowed at once are going',
  daily_cap: 'Today’s call limit is reached',
  already_dialing: 'Already being dialled',
  waiting_for_slots: 'Waiting for open slots',
  workflow_unavailable: 'Its workflow could not be loaded; VagusX has been told',
  no_outbound_number: 'The practice has no number to call from; VagusX has been told',

  // How a run ended (terminal reasons).
  met_on_call: 'Goal met on a call',
  met_on_return_call: 'Goal met when they called back',
  met_elsewhere: 'Goal met another way',
  no_longer_qualifies: 'No longer qualifies',
  exhausted: 'Every try used, never reached',
  stopped_on_outcome: 'Stopped by how a call ended',
  expired: 'The campaign’s time limit passed',
  expired_while_held: 'The time limit passed while another campaign went first',
  declined: 'Said no',
  wrong_number: 'Wrong number',
  engaged_no_booking: 'Talked, nothing booked',
  escalated: 'Asked for a person',
  opted_out: 'Asked us to stop',
  dnc: 'Do not contact',
  consent: 'No consent on file',
  excluded: 'Taken out of this campaign',
  stopped_by_user: 'Stopped by staff',
  error: 'Something went wrong; VagusX has been told',
};

/** The lanes, in the order the queue reads, with what each means. */
export const LANES: ReadonlyArray<{
  lane: QueueLane;
  label: string;
  hint: string;
  /** Set apart from the main flow: a detour, not a stage. */
  aside?: boolean;
}> = [
  {
    lane: 'ready',
    label: 'Ready',
    hint: 'Qualifies and can be called. Iris starts on them at the next membership check.',
  },
  { lane: 'waiting', label: 'Waiting', hint: 'Ready. The next try is scheduled.' },
  {
    lane: 'not_ready',
    label: 'Not ready',
    hint: 'Qualifies, but something is missing before Iris can call.',
  },
  { lane: 'calling', label: 'Calling now', hint: 'On a call right now.' },
  {
    lane: 'needs_person',
    label: 'Needs a person',
    hint: 'Iris has done what she can; the referral is back with the front desk.',
  },
  { lane: 'done', label: 'Done', hint: 'Booked, declined, opted out or taken out.' },
  {
    lane: 'callback',
    label: 'Callback set',
    hint: 'They asked for a time. The schedule pauses until then.',
    aside: true,
  },
  {
    lane: 'hold',
    label: 'On hold',
    hint: 'Started, then lost something it needs. Resumes on its own.',
    aside: true,
  },
];

/**
 * What the Ready lane means depends on whether the campaign is calling: only
 * an active campaign starts runs, so in a draft or a paused one its patients
 * stay here until it is launched or resumed, and while the practice has
 * automated calling switched off a started run places no call (ADR-341).
 */
export function laneHint(
  lane: QueueLane,
  status: OutreachCampaign['status'],
  practiceCalling = true
): string {
  if (lane === 'ready') {
    if (status === 'paused') {
      return 'Qualifies and can be called. Iris starts on them when the campaign resumes.';
    }
    if (status !== 'active') {
      return 'Qualifies and can be called. Iris starts on them once the campaign is launched.';
    }
    if (!practiceCalling) {
      return 'Qualifies and can be called. No calls go out while automated calling is off for the practice.';
    }
  }
  return LANES.find((l) => l.lane === lane)?.hint ?? '';
}

/** The lane the queue opens on: a campaign that is not calling has no runs to wait. */
export function initialLane(status: OutreachCampaign['status']): QueueLane {
  return status === 'active' ? 'waiting' : 'ready';
}
